import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { colors, baseStyles } from '../styles';
import { SearchBar, CategoryButton, CollapsibleSection, PlaceCard, ProviderCard, AgregarLugarModal, LocationPrompt, LocationIndicator } from '../components';
import { categoriasMascota, getPerfilById } from '../data/fakeData';
import { getFavorites } from '../services/favoritesService';
import { resolveStoredLocation } from '../services/locationService';

const styles = {
  ...baseStyles,
  categoriesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '0.5rem',
  },
  favoritosSection: {
    marginTop: '1.5rem',
    paddingTop: '1rem',
    borderTop: `1px solid ${colors.border}`,
  },
  favoritosTitle: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
  },
  starIcon: {
    color: '#fbbf24',
    fontSize: '0.875rem',
  },
  emptyState: {
    textAlign: 'center',
    padding: '2rem 1rem',
    color: colors.textMuted,
    fontSize: '0.875rem',
  },
};

export default function LocalizadorMascota() {
  const navigate = useNavigate();
  const { perfilId } = useParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [showAgregarModal, setShowAgregarModal] = useState(false);
  const [favoritos, setFavoritos] = useState([]);
  const [loadingFavoritos, setLoadingFavoritos] = useState(true);

  // Estado de ubicación
  const [userLocation, setUserLocation] = useState(null);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);

  const perfil = getPerfilById(perfilId);
  const nombrePerfil = perfil?.nombre || 'tu mascota';

  // Cargar ubicación guardada al montar (resuelve nombre si es GPS)
  useEffect(() => {
    resolveStoredLocation().then(location => {
      if (location) {
        setUserLocation(location);
      } else {
        setShowLocationPrompt(true);
      }
    });
  }, []);

  // Manejar cuando el usuario establece su ubicación
  const handleLocationSet = (location) => {
    setUserLocation(location);
    setShowLocationPrompt(false);
  };

  // Manejar cambio de ubicación
  const handleChangeLocation = () => {
    setShowLocationPrompt(true);
  };

  // Cargar favoritos del perfil
  const cargarFavoritos = useCallback(async () => {
    setLoadingFavoritos(true);
    try {
      const allFavoritos = await getFavorites();
      // Filtrar solo los favoritos de este perfil
      const favoritosPerfil = allFavoritos.filter(f => f.perfilId === perfilId);
      setFavoritos(favoritosPerfil);
    } catch (err) {
      console.error('Error cargando favoritos:', err);
    } finally {
      setLoadingFavoritos(false);
    }
  }, [perfilId]);

  useEffect(() => {
    cargarFavoritos();
  }, [cargarFavoritos]);

  // Obtener lugares de favoritos
  const lugaresFavoritos = favoritos.filter(f => f.lugar).map(f => ({
    ...f.lugar,
    favoriteId: f.id
  }));

  const handleCategoryClick = (categoria) => {
    navigate(`/localizador/resultados/${categoria.id}?perfil=${perfilId}`);
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Filtrar categorías basado en búsqueda
  const filterCategories = (categories) => {
    if (!searchQuery.trim()) return categories;
    const query = searchQuery.toLowerCase();
    return categories.filter(cat =>
      cat.nombre.toLowerCase().includes(query)
    );
  };

  // Categorías filtradas
  const filteredEstablecimientos = useMemo(
    () => filterCategories(categoriasMascota.establecimientos),
    [searchQuery]
  );
  const filteredProveedores = useMemo(
    () => filterCategories(categoriasMascota.proveedores),
    [searchQuery]
  );

  const totalResults = filteredEstablecimientos.length + filteredProveedores.length;
  const hasSearch = searchQuery.trim().length > 0;

  return (
    <div style={styles.container}>
      <div style={styles.main}>
        {/* Header */}
        <div style={styles.header}>
          <button style={styles.backButton} onClick={handleBack}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={colors.textPrimary} strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </button>
          <div>
            <h1 style={styles.title}>Servicios para {nombrePerfil}</h1>
          </div>
        </div>

        {/* Barra de búsqueda */}
        <SearchBar
          placeholder="Buscar categorías..."
          value={searchQuery}
          onChange={setSearchQuery}
        />

        {/* Indicador de ubicación */}
        <LocationIndicator
          location={userLocation}
          onChangeClick={handleChangeLocation}
        />

        {/* Sin resultados */}
        {hasSearch && totalResults === 0 && (
          <div style={styles.emptyState}>
            No se encontraron categorías para "{searchQuery}"
          </div>
        )}

        {/* Sección Establecimientos */}
        {filteredEstablecimientos.length > 0 && (
          <CollapsibleSection title="Establecimientos" defaultOpen={true}>
            <div style={styles.categoriesGrid}>
              {filteredEstablecimientos.map((cat) => (
                <CategoryButton
                  key={cat.id}
                  categoria={cat}
                  onClick={handleCategoryClick}
                />
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Sección Proveedores Independientes */}
        {filteredProveedores.length > 0 && (
          <CollapsibleSection title="Proveedores Independientes" defaultOpen={!hasSearch && filteredEstablecimientos.length === 0}>
            <div style={styles.categoriesGrid}>
              {filteredProveedores.map((cat) => (
                <CategoryButton
                  key={cat.id}
                  categoria={cat}
                  onClick={handleCategoryClick}
                />
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Sección de Favoritos */}
        {!loadingFavoritos && lugaresFavoritos.length > 0 && (
          <div style={styles.favoritosSection}>
            <h2 style={styles.favoritosTitle}>
              <span style={styles.starIcon}>★</span>
              Favoritos de {nombrePerfil}
            </h2>
            {lugaresFavoritos.map((lugar) => (
              (lugar.tipo === 'independiente' || lugar.tipo === 'proveedor') ? (
                <ProviderCard
                  key={lugar.id}
                  proveedor={lugar}
                  showActions={true}
                  perfilId={perfilId}
                />
              ) : (
                <PlaceCard
                  key={lugar.id}
                  lugar={lugar}
                  showActions={true}
                  perfilId={perfilId}
                />
              )
            ))}
          </div>
        )}

        {/* Botón para agregar lugar */}
        <button
          onClick={() => setShowAgregarModal(true)}
          style={{
            width: '100%',
            padding: '0.875rem',
            marginTop: '1.5rem',
            backgroundColor: colors.surface,
            border: `1px dashed ${colors.border}`,
            borderRadius: '10px',
            fontSize: '0.875rem',
            color: colors.textSecondary,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}
        >
          <span style={{ fontSize: '1.125rem' }}>+</span>
          Agregar un lugar que conozco
        </button>

        {/* Modal para agregar lugar */}
        <AgregarLugarModal
          isOpen={showAgregarModal}
          onClose={() => setShowAgregarModal(false)}
        />

        {/* Modal de ubicación */}
        <LocationPrompt
          isOpen={showLocationPrompt}
          onLocationSet={handleLocationSet}
          onClose={() => setShowLocationPrompt(false)}
        />
      </div>
    </div>
  );
}
